// @ts-nocheck
import React, { useEffect, useRef, useState } from "react";
import {
  trackSponsorImpression,
  trackSponsorClick,
  type SponsorPlacement,
} from "@/lib/sponsorTracking";

interface SponsorLogoProps {
  sponsor: { id: string; name?: string | null; logo_url?: string | null; website_url?: string | null };
  placement: SponsorPlacement;
  eventId?: string | null;
  /** Tailwind classes for the rendered <img> */
  imgClassName?: string;
  /** Tailwind classes for the wrapper anchor/span */
  className?: string;
  children?: React.ReactNode;
}

/**
 * Renders a sponsor logo (or name fallback) and wires up analytics:
 * - Logs an impression once when it first scrolls into view.
 * - Logs a click before navigating to the sponsor website.
 */
export const SponsorLogo: React.FC<SponsorLogoProps> = ({
  sponsor,
  placement,
  eventId,
  imgClassName = "h-36 w-auto object-contain",
  className = "",
  children,
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [seen, setSeen] = useState(false);

  useEffect(() => {
    if (!sponsor?.id || seen || !ref.current) return;
    const el = ref.current;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setSeen(true);
            trackSponsorImpression(sponsor.id, placement, eventId);
            observer.disconnect();
            break;
          }
        }
      },
      { threshold: 0.5 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [sponsor?.id, placement, eventId, seen]);

  const handleClick = (e: React.MouseEvent) => {
    if (!sponsor.website_url) return;
    e.preventDefault();
    trackSponsorClick(sponsor.id, placement, sponsor.website_url, eventId).finally(() => {
      window.open(sponsor.website_url!, "_blank", "noopener,noreferrer");
    });
  };

  const content =
    children ??
    (sponsor.logo_url ? (
      <img src={sponsor.logo_url} alt={sponsor.name || "Sponsor"} className={imgClassName} />
    ) : (
      <span className="text-sm font-medium">{sponsor.name}</span>
    ));

  if (sponsor.website_url) {
    return (
      <div ref={ref} className={className}>
        <a
          href={sponsor.website_url}
          onClick={handleClick}
          target="_blank"
          rel="noopener noreferrer"
          className="hover:opacity-80 transition-opacity"
        >
          {content}
        </a>
      </div>
    );
  }

  return (
    <div ref={ref} className={className}>
      {content}
    </div>
  );
};

export default SponsorLogo;
